import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import Box from 'core-system/Box/Box'
import NorthIcon from '@mui/icons-material/North'
import palette from 'core-system/Themes/palette'
import { TimeframeOption } from '../adminDashboardTypes'

const SectionTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${(props) => props.theme.breakpoints[0]}) {
    grid-template-columns: 1fr;
  }
`

const StatCard = styled(Box)`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.palette.white};
`

const StatLabel = styled(Text)`
  color: ${(props) => props.theme.palette.text.secondary};
  margin-bottom: 0.5rem;
  font-size: 0.8125rem;
`

const StatValueContainer = styled.div`
  display: flex;
  align-items: baseline;
  margin-bottom: 0.75rem;
  min-height: 2.5rem;
`

const StatValue = styled(Text)`
  font-weight: 600;
  font-size: 2rem;
  line-height: 1.2;
`

const StatSubtitle = styled.span`
  font-size: 0.875rem;
  font-weight: 400;
  color: ${palette.text.secondary};
  margin-left: 0.375rem;
`

type TrendVariant = 'positive' | 'negative' | 'neutral'

const TrendBadge = styled.div<{ $variant: TrendVariant }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  width: fit-content;
  background-color: ${({ $variant }) => {
    if ($variant === 'positive') return palette.success.green100
    if ($variant === 'negative') return palette.error.red100
    return palette.grey.grey200
  }};
  color: ${({ $variant }) => {
    if ($variant === 'positive') return palette.success.green700
    if ($variant === 'negative') return palette.error.red700
    return palette.grey.grey700
  }};
`

interface AtAGlanceData {
  eligibleCommuters: number
  enrolledCommuters: number
  participationRate: number
  taxSavings: number
  carbonReduction: number
  trends: {
    eligible: { value: number; label: string }
    enrolled: { value: number; label: string }
    participation: { value: number; label: string }
    taxSavings: { value: string; label: string }
    carbon: { value: string; label: string }
  }
}

interface AtAGlanceGridProps {
  data: AtAGlanceData
  selectedTimeframe: TimeframeOption
}

const AtAGlanceGrid = ({ data, selectedTimeframe }: AtAGlanceGridProps) => {
  const getVariant = (value: number): TrendVariant => {
    if (value > 0) return 'positive'
    if (value < 0) return 'negative'
    return 'neutral'
  }

  const formatDollar = (cents: number): string => {
    if (!cents) return '$0'
    if (cents >= 1000) {
      return `$${Math.round(cents / 1000)}k`
    }
    return `$${Math.round(cents)}`
  }

  const stats = [
    {
      label: 'Eligible Commuters',
      value: data.eligibleCommuters.toLocaleString(),
      trendValue: data.trends.eligible.label,
      trendVariant: getVariant(data.trends.eligible.value),
    },
    {
      label: 'Enrolled Commuters',
      value: data.enrolledCommuters.toLocaleString(),
      trendValue: data.trends.enrolled.label,
      trendVariant: getVariant(data.trends.enrolled.value),
    },
    {
      label: 'Participation Rate',
      value: `${data.participationRate}%`,
      trendValue: data.trends.participation.label,
      trendVariant: getVariant(data.trends.participation.value),
    },
    {
      label: 'Tax Savings',
      value: formatDollar(data.taxSavings),
      subtitle:
        selectedTimeframe === '7d'
          ? 'this week'
          : selectedTimeframe === '30d'
            ? 'this month'
            : 'this quarter',
      trendValue: data.trends.taxSavings.label,
      trendVariant: 'positive' as TrendVariant,
    },
    {
      label: 'Carbon Reduction',
      value: `${data.carbonReduction}`,
      subtitle: 'kg CO\u2082',
      trendValue: data.trends.carbon.label,
      trendVariant: 'positive' as TrendVariant,
    },
  ]

  const getArrowRotation = (variant: TrendVariant) => {
    if (variant === 'positive') return 'rotate(0deg)'
    if (variant === 'negative') return 'rotate(180deg)'
    return 'rotate(90deg)'
  }

  return (
    <>
      <SectionTitle variant='h4'>At a Glance</SectionTitle>
      <Grid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatLabel variant='body2'>{stat.label}</StatLabel>
            <StatValueContainer>
              <StatValue variant='h3'>{stat.value}</StatValue>
              {stat.subtitle && <StatSubtitle>{stat.subtitle}</StatSubtitle>}
            </StatValueContainer>
            <TrendBadge $variant={stat.trendVariant}>
              <NorthIcon
                style={{
                  fontSize: '0.75rem',
                  transform: getArrowRotation(stat.trendVariant),
                }}
              />
              {stat.trendValue}
            </TrendBadge>
          </StatCard>
        ))}
      </Grid>
    </>
  )
}

export default React.memo(AtAGlanceGrid)
